import { useTranslations } from "@acto/web-common/locales";
import { styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import type { DateRange as MuiDateRange } from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useState } from "react";
import type { DateRange } from "./types";

interface DateRangeCalendarModalProps {
  initialDateRange?: DateRange;
  onApply: (dateRange: DateRange) => void;
  onCancel: () => void;
}

const Overlay = styled(Dialog.Overlay)({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  zIndex: 10010,
});

const Content = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.managementView.colors.neutral[0],
  borderRadius: theme.managementView.radius[16],
  boxShadow: theme.shadows[3],
  padding: theme.managementView.spacing[24],
  zIndex: 10011,
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflow: "auto",
}));

const CalendarWrapper = styled("div")(({ theme }) => ({
  "& .MuiDateRangeCalendar-root": {
    fontFamily: theme.managementView.font.family,
  },
  "& .MuiPickersCalendarHeader-label": {
    fontFamily: theme.managementView.font.family,
    fontSize: theme.managementView.font.size[14],
    fontWeight: theme.managementView.font.weight[500],
  },
  "& .MuiDayCalendar-weekDayLabel": {
    fontFamily: theme.managementView.font.family,
    fontSize: theme.managementView.font.size[13],
    color: theme.managementView.colors.neutral[56],
  },
  "& .MuiPickersDay-root": {
    fontFamily: theme.managementView.font.family,
    fontSize: theme.managementView.font.size[14],
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: theme.colors.brand[500],
  },
  "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": {
    backgroundColor: `${theme.colors.brand[100]}`,
  },
}));

const ButtonGroup = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.managementView.spacing[12],
  marginTop: theme.managementView.spacing[16],
  borderTop: `1px solid ${theme.managementView.colors.neutral[8]}`,
  paddingTop: theme.managementView.spacing[16],
}));

const Button = styled("button")<{ variant?: "primary" | "secondary" }>(({ theme }) => ({
  height: "40px",
  padding: `0 ${theme.managementView.spacing[24]}`,
  borderRadius: "50px",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  cursor: "pointer",
  transition: "background-color 0.2s",
  variants: [
    {
      props: { variant: "secondary" },
      style: {
        backgroundColor: "transparent",
        border: `1px solid ${theme.managementView.colors.neutral[8]}`,
        color: theme.managementView.colors.neutral[56],
        "&:hover": {
          backgroundColor: theme.managementView.colors.neutral[4],
        },
      },
    },
    {
      props: { variant: "primary" },
      style: {
        backgroundColor: theme.colors.brand[500],
        border: "none",
        color: theme.managementView.colors.neutral[0],
        "&:hover": {
          backgroundColor: theme.colors.brand[600],
        },
        "&:disabled": {
          backgroundColor: theme.managementView.colors.neutral[24],
          cursor: "not-allowed",
        },
      },
    },
  ],
}));

const DATE_FORMAT = "YYYY-MM-DD";

export const DateRangeCalendarModal = ({ initialDateRange, onApply, onCancel }: DateRangeCalendarModalProps) => {
  const t = useTranslations("common");
  const tDateRange = useTranslations("dateRange");

  const [dateRange, setDateRange] = useState<MuiDateRange<Dayjs>>(() => {
    if (initialDateRange) {
      return [dayjs(initialDateRange.startDate), dayjs(initialDateRange.endDate)];
    }
    return [null, null];
  });

  const minDate = dayjs().subtract(12, "month");
  const maxDate = dayjs();

  const handleDateChange = (newDateRange: MuiDateRange<Dayjs>) => {
    setDateRange(newDateRange);
  };

  const handleApply = () => {
    const [start, end] = dateRange;
    if (start && end) {
      onApply({
        startDate: start.format(DATE_FORMAT),
        endDate: end.format(DATE_FORMAT),
      });
    }
  };

  const isApplyDisabled = !dateRange[0] || !dateRange[1];

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Overlay />
        <Content>
          <Dialog.Title className="visually-hidden">{tDateRange("selectRange")}</Dialog.Title>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CalendarWrapper>
              <DateRangeCalendar
                value={dateRange}
                onChange={handleDateChange}
                calendars={1}
                minDate={minDate}
                maxDate={maxDate}
              />
            </CalendarWrapper>
          </LocalizationProvider>
          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t("cancel")}
            </Button>
            <Button type="button" variant="primary" onClick={handleApply} disabled={isApplyDisabled}>
              {t("apply")}
            </Button>
          </ButtonGroup>
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
